import { useEffect, useRef, useState } from "react";
import type { SchemaElement } from "../../../../api/restaurantSchema";
import {
  createTableRotationGuard,
  type TableRotationStop,
} from "../../../../components/restaurant-schema/tableCollision";

// Близо до кратно на 45° (0°, 45°, 90°…) ъгълът „залепва" за него.
const SNAP_ANGLE = 45;
const SNAP_TOLERANCE = 4;
// Със Shift — на стъпки по 15°.
const SHIFT_STEP = 15;
// Колко стои показаният ъгъл след пускане, докато кешът го поеме (като влаченето).
const SETTLE_MS = 400;

type RotateSession = {
  elementId: number;
  pointerId: number;
  // Центърът на елемента в екранни координати.
  centerX: number;
  centerY: number;
  startPointerAngle: number;
  startRotation: number;
  // Колко далеч от центъра стои дръжката, докато се върти (пиксели).
  radius: number;
  // Само за маса: докъде наистина се завърта, без да влезе в съседна маса.
  guardRotation: ((target: number) => TableRotationStop) | null;
  // Масата, която спира завъртането при последното движение (null — следва курсора).
  blocker: SchemaElement | null;
};

export type RotationPreview = {
  elementId: number;
  rotation: number;
  // Посоката от центъра към дръжката (градуси, екранни — 90° е надолу) и разстоянието.
  handleAngle: number;
  radius: number;
  active: boolean;
};

const normalize = (degrees: number) => ((Math.round(degrees) % 360) + 360) % 360;

const pointerAngle = (session: Pick<RotateSession, "centerX" | "centerY">, clientX: number, clientY: number) =>
  (Math.atan2(clientY - session.centerY, clientX - session.centerX) * 180) / Math.PI;

/** Ъгълът с „залепване": Shift → по 15°, иначе близо до кратно на 45° → точно него. */
export const snapRotation = (degrees: number, withShift: boolean) => {
  if (withShift) return normalize(Math.round(degrees / SHIFT_STEP) * SHIFT_STEP);
  const nearest = Math.round(degrees / SNAP_ANGLE) * SNAP_ANGLE;
  return normalize(Math.abs(degrees - nearest) <= SNAP_TOLERANCE ? nearest : degrees);
};

// Най-краткият път от `from` до `to` в градуси (−180…180).
const shortestTurn = (from: number, to: number) => ((((to - from) % 360) + 540) % 360) - 180;

/**
 * Посоката на дръжката: след курсора, докато елементът го следва; спряна ли е
 * масата от съседна маса — там, докъдето масата наистина се е завъртяла
 * (дръжката не „бяга" напред от нея).
 */
export const handleAngleFor = (
  session: { startPointerAngle: number; startRotation: number },
  cursorAngle: number,
  stop: TableRotationStop,
) =>
  stop.blocker ? session.startPointerAngle + shortestTurn(session.startRotation, stop.rotation) : cursorAngle;

/**
 * Свободно завъртане с мишката/пръста: дръжката под избрания елемент се
 * хваща и се върти около центъра му — ъгълът следва курсора плавно (без
 * предефинирани стъпки), само близо до 0°/45°/90°… залепва. Маса спира при
 * първия ъгъл, в който би влязла в съседна маса (`createTableRotationGuard`),
 * и дръжката спира с нея; пусната така, `onBlocked` казва коя маса пречи
 * (същото „Няма място…" като R/±45°). Докато се върти, `preview` дава ъгъла
 * за рисуване; при пускане `onRotate` записва докъдето масата е стигнала.
 */
export const useElementRotate = ({
  elements,
  onRotate,
  onBlocked,
}: {
  elements: SchemaElement[];
  onRotate: (element: SchemaElement, rotation: number) => void;
  onBlocked: (blocker: SchemaElement) => void;
}) => {
  const sessionRef = useRef<RotateSession | null>(null);
  const settleTimerRef = useRef<number | null>(null);
  const [preview, setPreviewState] = useState<RotationPreview | null>(null);
  const previewRef = useRef<RotationPreview | null>(null);
  const setPreview = (next: RotationPreview | null) => {
    previewRef.current = next;
    setPreviewState(next);
  };

  useEffect(
    () => () => {
      if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
    },
    [],
  );

  /**
   * `center` — центърът на елемента, `handle` — дръжката, и двете в пиксели
   * на платното (спрямо горния му ляв ъгъл).
   */
  const bindHandle = (
    element: SchemaElement,
    center: { x: number; y: number },
    handle: { x: number; y: number },
  ) => ({
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      event.stopPropagation();
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      if (settleTimerRef.current !== null) {
        window.clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
      // Платното е най-близкият позициониран родител на дръжката.
      const canvas = (event.currentTarget.offsetParent ?? document.body).getBoundingClientRect();
      const session = {
        elementId: element.id,
        pointerId: event.pointerId,
        centerX: canvas.left + center.x,
        centerY: canvas.top + center.y,
        startPointerAngle: 0,
        startRotation: element.rotation,
        radius: Math.hypot(handle.x - center.x, handle.y - center.y),
        guardRotation: element.kind === "table" ? createTableRotationGuard(element, elements) : null,
        blocker: null,
      };
      session.startPointerAngle = pointerAngle(session, event.clientX, event.clientY);
      sessionRef.current = session;
      setPreview({
        elementId: element.id,
        rotation: element.rotation,
        handleAngle: session.startPointerAngle,
        radius: session.radius,
        active: true,
      });
    },
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => {
      const session = sessionRef.current;
      if (!session || session.pointerId !== event.pointerId) return;
      const angle = pointerAngle(session, event.clientX, event.clientY);
      const target = snapRotation(session.startRotation + angle - session.startPointerAngle, event.shiftKey);
      const stop = session.guardRotation ? session.guardRotation(target) : { rotation: target, blocker: null };
      session.blocker = stop.blocker;
      setPreview({
        elementId: session.elementId,
        rotation: stop.rotation,
        handleAngle: handleAngleFor(session, angle, stop),
        radius: session.radius,
        active: true,
      });
    },
    onPointerUp: (event: React.PointerEvent<HTMLElement>) => {
      const session = sessionRef.current;
      if (!session || session.pointerId !== event.pointerId) return;
      sessionRef.current = null;
      const current = previewRef.current;
      if (!current) return;
      if (current.rotation !== normalize(session.startRotation)) onRotate(element, current.rotation);
      // Пусната, докато съседна маса я спира — казва защо не е стигнала курсора.
      if (session.blocker) onBlocked(session.blocker);
      setPreview({ ...current, active: false });
      settleTimerRef.current = window.setTimeout(() => {
        settleTimerRef.current = null;
        setPreview(null);
      }, SETTLE_MS);
    },
    onPointerCancel: () => {
      sessionRef.current = null;
      setPreview(null);
    },
  });

  return { preview, bindHandle };
};
